import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { loadGoogleMapsApi } from '../utils/googleMapsLoader';

// Déclaration du type Google Maps pour éviter les erreurs TypeScript
//declare global {
//  interface Window {
//    google: any;
//    initMap: () => void;
//  }
//}

export interface GoogleMapProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  width?: number;
  height?: number;
  mapType?: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
  showMarker?: boolean;
  markerTitle?: string;
  address?: string;
  apiKey: string;
  className?: string;
  style?: React.CSSProperties; // ✅ ajouté
  onMarkerDragStart?: () => void;
  onMarkerDrag?: (lat: number, lng: number) => void;
  onMarkerDragEnd?: (lat: number, lng: number) => void;
  draggableMarker?: boolean;
  mapId?: string; // ✅ ajouté
  // Nouvelles propriétés pour les fonctionnalités avancées
  additionalMarkers?: Array<{
    id: string;
    latitude: number;
    longitude: number;
    title: string;
    description?: string;
    color?: string;
    icon?: string;
  }>;
  enableStreetView?: boolean;
  enableZoomControl?: boolean;
  enableMapTypeControl?: boolean;
  enableFullscreenControl?: boolean;
  markerColor?: string;
  markerIcon?: string;
  customMarkerUrl?: string;
  showDirections?: boolean;
  directionsMode?: 'driving' | 'walking' | 'cycling' | 'transit';
  showDragHint?: boolean; // ✅ ajout
  geolocationRequestKey?: number;
}

const createSafeIntersectionObserver = (
  callback: IntersectionObserverCallback,
  options?: IntersectionObserverInit
): IntersectionObserver | null => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return null;
  return new IntersectionObserver(callback, options);
};

const observeIfElement = (observer: IntersectionObserver | null, target: Element | null) => {
  if (!observer || !target) return;
  observer.observe(target);
};

const unobserveIfElement = (observer: IntersectionObserver | null, target: Element | null) => {
  if (!observer || !target) return;
  observer.unobserve(target);
  observer.disconnect();
};

const buildInfoWindowContent = (
  title: string,
  address: string,
  latitude: number,
  longitude: number,
  draggable: boolean,
  description?: string,
  showDragHint: boolean = false
) => `
  <div style="padding: 8px;">
    <h6>${title}</h6>
    ${address ? `<p style="margin: 4px 0; color: #666;">${address}</p>` : ''}
    ${description ? `<p style="margin: 4px 0; color: #666;">${description}</p>` : ''}
    <small style="color: #999;">Lat: ${latitude}, Lng: ${longitude}</small>
    ${draggable && showDragHint ? '<br><small style="color: #007bff;">📍 Déplacez-moi pour ajuster la position</small>' : ''}
  </div>
`;

const GoogleMap: React.FC<GoogleMapProps> = ({
  latitude,
  longitude,
  zoom = 14,
  width = 500,
  height = 300,
  mapType = 'roadmap',
  showMarker = true,
  markerTitle = '',
  address = '',
  apiKey,
  className = '',
  style = {}, // ✅ ajouté
  onMarkerDragStart,
  onMarkerDrag,
  onMarkerDragEnd,
  draggableMarker = false,
  mapId, // ✅ ajouté
  // Nouvelles propriétés
  additionalMarkers = [],
  enableStreetView = true,
  enableZoomControl = true,
  enableMapTypeControl = false,
  enableFullscreenControl = false,
  markerColor = '#FF0000',
  markerIcon = 'default',
  customMarkerUrl,
  showDirections = false,
  directionsMode = 'driving',
  showDragHint = false,
  geolocationRequestKey = 0,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const additionalMarkersRef = useRef<any[]>([]);
  const directionsServiceRef = useRef<any>(null);
  const directionsRendererRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userPosition, setUserPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [directionsError, setDirectionsError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const googleMapTypeId = useMemo(() => {
    switch (mapType) {
      case 'satellite':
        return window.google?.maps?.MapTypeId?.SATELLITE;
      case 'hybrid':
        return window.google?.maps?.MapTypeId?.HYBRID;
      case 'terrain':
        return window.google?.maps?.MapTypeId?.TERRAIN;
      default:
        return window.google?.maps?.MapTypeId?.ROADMAP;
    }
  }, [mapType]);

  // Fonction pour charger l'API Google Maps
  const loadGoogleMapsAPI = useCallback((): Promise<void> => {
    return loadGoogleMapsApi(apiKey);
  }, [apiKey]);

  // Fonction pour obtenir l'icône du marqueur
  const getMarkerIcon = useCallback((icon: string, color: string, customUrl?: string) => {
    if (icon === 'custom' && customUrl) {
      return {
        url: customUrl,
        scaledSize: new window.google.maps.Size(32, 32),
      };
    }

    // Icônes prédéfinies avec émojis (conversion en icône Google Maps)
    const iconMap: { [key: string]: string } = {
      'default': '',
      'home': '🏠',
      'office': '🏢',
      'store': '🏪',
      'restaurant': '🍽️',
      'hotel': '🏨',
      'school': '🏫',
      'hospital': '🏥',
      'park': '🌳'
    };

    const emoji = iconMap[icon] || '';
    if (emoji) {
      // Créer une icône SVG avec l'émoji
      const svgIcon = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="32" height="32" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="15" fill="${color}" stroke="#fff" stroke-width="2"/>
          <text x="16" y="20" text-anchor="middle" font-size="12" fill="#fff">${emoji}</text>
        </svg>
      `)}`;
      
      return {
        url: svgIcon,
        scaledSize: new window.google.maps.Size(32, 32),
      };
    }

    // Icône par défaut avec couleur
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="32" height="40" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.2 0 0 7.2 0 16c0 8.8 16 24 16 24s16-15.2 16-24c0-8.8-7.2-16-16-16z" fill="${color}" stroke="#fff" stroke-width="2"/>
          <circle cx="16" cy="16" r="6" fill="#fff"/>
        </svg>
      `)}`,
      scaledSize: new window.google.maps.Size(32, 40),
    };
  }, []);

  const createMarkerContent = useCallback((icon: { url: string } | null) => {
    if (!icon?.url) return undefined;
    const img = document.createElement('img');
    img.src = icon.url;
    img.width = 32;
    img.height = 32;
    return img;
  }, []);

  const clearMarker = (marker: any) => {
    if (!marker) return;
    if (typeof marker.setMap === 'function') marker.setMap(null);
    else marker.map = null;
  };

  

  // ✅ Garde UN SEUL initializeMap (supprime le duplicat plus haut)
  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google || !window.google.maps || !mountedRef.current) {
      return;
    }

    if (!mapRef.current.isConnected) {
      console.warn('GoogleMap: Element not connected to DOM yet');
      return;
    }

    try {
      const mapOptions = {
        center: { lat: latitude, lng: longitude },
        zoom,
        mapTypeId: googleMapTypeId,
        mapTypeControl: enableMapTypeControl,
        streetViewControl: enableStreetView,
        fullscreenControl: enableFullscreenControl,
        zoomControl: enableZoomControl,
        disableDefaultUI: false,
        gestureHandling: 'cooperative',
        ...(mapId ? { mapId } : {}),
      };

      const hasAdvanced = !!window.google.maps?.marker?.AdvancedMarkerElement && !!mapId;

      // Créer la carte avec gestion d'erreur
      try {
        const newMap = new window.google.maps.Map(mapRef.current, mapOptions);
        mapInstanceRef.current = newMap;

        // ✅ Ré-attacher le renderer si déjà créé
        if (directionsRendererRef.current) {
          directionsRendererRef.current.setMap(mapInstanceRef.current);
        }
      } catch (mapError) {
        console.error('Erreur lors de la création de la carte Google Maps:', mapError);
        if (mountedRef.current) setError('Erreur lors de la création de la carte');
        return;
      }

      // Nettoyer les anciens marqueurs
      if (markerRef.current) {
        clearMarker(markerRef.current);
      }
      additionalMarkersRef.current.forEach((marker) => clearMarker(marker));
      additionalMarkersRef.current = [];

      // Ajouter le marqueur principal si demandé
      if (showMarker && mapInstanceRef.current) {
        const icon = getMarkerIcon(markerIcon, markerColor, customMarkerUrl);

        if (hasAdvanced) {
          const content = createMarkerContent(icon || null);
          const marker = new window.google.maps.marker.AdvancedMarkerElement({
            map: mapInstanceRef.current,
            position: { lat: latitude, lng: longitude },
            title: markerTitle,
            content,
            gmpDraggable: draggableMarker,
          });
          markerRef.current = marker;

          if (draggableMarker) {
            marker.addListener('gmp-dragstart', () => onMarkerDragStart?.());
            marker.addListener('gmp-drag', (e: any) => onMarkerDrag?.(e.latLng.lat(), e.latLng.lng()));
            marker.addListener('gmp-dragend', (e: any) => onMarkerDragEnd?.(e.latLng.lat(), e.latLng.lng()));
          }

          if (address || markerTitle) {
            const infoWindow = new window.google.maps.InfoWindow({
              content: buildInfoWindowContent(markerTitle, address, latitude, longitude, draggableMarker, undefined, showDragHint),
            });

            marker.addListener('gmp-click', () => {
              infoWindow.open({ map: mapInstanceRef.current, anchor: marker });
            });
          }
        } else {
          const markerOptions: any = {
            position: { lat: latitude, lng: longitude },
            map: mapInstanceRef.current,
            title: markerTitle,
            animation: window.google.maps.Animation.DROP,
            draggable: draggableMarker,
          };
          if (icon) markerOptions.icon = icon;

          const marker = new window.google.maps.Marker(markerOptions);
          markerRef.current = marker;

          if (draggableMarker) {
            marker.addListener('dragstart', () => onMarkerDragStart?.());
            marker.addListener('drag', () => {
              const position = marker.getPosition();
              if (position) onMarkerDrag?.(position.lat(), position.lng());
            });
            marker.addListener('dragend', () => {
              const position = marker.getPosition();
              if (position) onMarkerDragEnd?.(position.lat(), position.lng());
            });
          }

          if (address || markerTitle) {
            const infoWindow = new window.google.maps.InfoWindow({
              content: buildInfoWindowContent(markerTitle, address, latitude, longitude, draggableMarker, undefined, showDragHint),
            });

            marker.addListener('click', () => {
              infoWindow.open(mapInstanceRef.current, marker);
            });
          }
        }
      }

      // Marqueurs additionnels
      additionalMarkers.forEach((markerData) => {
        const icon = getMarkerIcon(markerData.icon || 'default', markerData.color || '#FF0000');

        if (hasAdvanced) {
          const content = createMarkerContent(icon || null);
          const marker = new window.google.maps.marker.AdvancedMarkerElement({
            map: mapInstanceRef.current,
            position: { lat: markerData.latitude, lng: markerData.longitude },
            title: markerData.title,
            content,
          });
          additionalMarkersRef.current.push(marker);

          if (markerData.title || markerData.description) {
            const infoWindow = new window.google.maps.InfoWindow({
              content: buildInfoWindowContent(
                markerData.title,
                '',
                markerData.latitude,
                markerData.longitude,
                false,
                markerData.description
              ),
            });

            marker.addListener('gmp-click', () => {
              infoWindow.open({ map: mapInstanceRef.current, anchor: marker });
            });
          }
        } else {
          const markerOptions: any = {
            position: { lat: markerData.latitude, lng: markerData.longitude },
            map: mapInstanceRef.current,
            title: markerData.title,
            animation: window.google.maps.Animation.DROP,
          };
          if (icon) markerOptions.icon = icon;

          const marker = new window.google.maps.Marker(markerOptions);
          additionalMarkersRef.current.push(marker);

          if (markerData.title || markerData.description) {
            const infoWindow = new window.google.maps.InfoWindow({
              content: buildInfoWindowContent(
                markerData.title,
                '',
                markerData.latitude,
                markerData.longitude,
                false,
                markerData.description
              ),
            });

            marker.addListener('click', () => {
              infoWindow.open(mapInstanceRef.current, marker);
            });
          }
        }
      });

      // Ajuster la vue pour inclure tous les marqueurs si il y en a plusieurs
      if (!showDirections && additionalMarkers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        if (showMarker) bounds.extend({ lat: latitude, lng: longitude });
        additionalMarkers.forEach(marker => bounds.extend({ lat: marker.latitude, lng: marker.longitude }));
        mapInstanceRef.current.fitBounds(bounds);

        const listener = window.google.maps.event.addListener(mapInstanceRef.current, 'idle', () => {
          if (mapInstanceRef.current.getZoom() > 16) {
            mapInstanceRef.current.setZoom(16);
          }
          window.google.maps.event.removeListener(listener);
        });
      }
    } catch (err) {
      console.error('Erreur lors de l\'initialisation de la carte:', err);
      if (mountedRef.current) setError('Erreur lors de l\'initialisation de la carte');
    }
  }, [
    latitude,
    longitude,
    zoom,
    mapId,
    enableMapTypeControl,
    enableStreetView,
    enableFullscreenControl,
    enableZoomControl,
    mapType,
    showMarker,
    markerTitle,
    address,
    additionalMarkers,
    markerColor,
    markerIcon,
    customMarkerUrl,
    getMarkerIcon,
    googleMapTypeId,
    showDirections
  ]);

  // Effet pour charger l'API et initialiser la carte
  useEffect(() => {
    mountedRef.current = true;

    if (!apiKey) {
      setError('Clé API Google Maps manquante');
      return;
    }

    // Reset de l'état
    setError(null);
    setIsLoaded(false);

    loadGoogleMapsAPI()
      .then(() => {
        if (mountedRef.current) {
          setIsLoaded(true);
        }
      })
      .catch((err) => {
        console.error('Erreur lors du chargement de Google Maps:', err);
        if (mountedRef.current) {
          setError('Impossible de charger Google Maps');
        }
      });

    return () => {
      mountedRef.current = false;
    };
  }, [apiKey, loadGoogleMapsAPI]);

  // Effet pour initialiser la carte quand elle est chargée
  useEffect(() => {
    if (isLoaded && mountedRef.current) {
      // Attendre un peu pour s'assurer que le DOM est prêt
      const timer = setTimeout(() => {
        if (mountedRef.current && mapRef.current && mapRef.current.isConnected) {
          initializeMap();
        } else if (mountedRef.current) {
          // Retry après un délai plus long si l'élément n'est pas encore connecté
          const retryTimer = setTimeout(() => {
            if (mountedRef.current) {
              initializeMap();
            }
          }, 200);
          return () => clearTimeout(retryTimer);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isLoaded, initializeMap]);

  // Effet pour mettre à jour la carte quand les props changent
  useEffect(() => {
    if (mapInstanceRef.current && isLoaded && mountedRef.current) {
      // Réinitialiser la carte avec les nouveaux paramètres
      initializeMap();
    }
  }, [latitude, longitude, zoom, mapType, markerTitle, isLoaded, additionalMarkers, showMarker, markerColor, markerIcon, customMarkerUrl, initializeMap]);

  // Effet pour mettre à jour la position du marqueur principal sans réinitialiser toute la carte
  useEffect(() => {
    if (markerRef.current && mapInstanceRef.current && isLoaded && !draggableMarker) {
      // Mettre à jour seulement la position du marqueur si ce n'est pas en cours de déplacement
      const newPosition = { lat: latitude, lng: longitude };
      markerRef.current.setPosition(newPosition);
      
      // Centrer la carte sur la nouvelle position
      mapInstanceRef.current.setCenter(newPosition);
    }
  }, [latitude, longitude, isLoaded, draggableMarker]);

  // Effet pour gérer l'affichage des directions
  useEffect(() => {
    if (!showDirections) {
      setUserPosition(null);
      return;
    }
    if (!navigator.geolocation) {
      console.warn('Geolocation non supportée par ce navigateur');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => console.warn('Erreur géolocalisation:', err),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [showDirections]);

  useEffect(() => {
    if (!isLoaded || !mapInstanceRef.current) return;

    if (showDirections) {
      if (!directionsRendererRef.current) {
        directionsRendererRef.current = new window.google.maps.DirectionsRenderer();
      }
      directionsRendererRef.current.setOptions({ preserveViewport: false, suppressMarkers: false });
      directionsRendererRef.current.setMap(mapInstanceRef.current);
    } else if (directionsRendererRef.current) {
      directionsRendererRef.current.setDirections({ routes: [] } as any);
      directionsRendererRef.current.setMap(null);
      directionsRendererRef.current = null;
    }
  }, [isLoaded, showDirections]);

  useEffect(() => {
    if (!showDirections || !isLoaded || !mapInstanceRef.current || !userPosition) return;

    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }

    const travelModeMap: Record<string, any> = {
      driving: window.google.maps.TravelMode.DRIVING,
      walking: window.google.maps.TravelMode.WALKING,
      cycling: window.google.maps.TravelMode.BICYCLING,
      transit: window.google.maps.TravelMode.TRANSIT,
    };

    directionsServiceRef.current.route(
      {
        origin: userPosition,
        destination: { lat: latitude, lng: longitude },
        travelMode: travelModeMap[directionsMode] || window.google.maps.TravelMode.DRIVING,
      },
      (result: any, status: string) => {
        if (status === 'OK' && directionsRendererRef.current) {
          setDirectionsError(null);
          directionsRendererRef.current.setDirections(result);
        } else {
          setDirectionsError(status);
          console.warn('DirectionsService status:', status);
        }
      }
    );
  }, [showDirections, directionsMode, userPosition, isLoaded, latitude, longitude]);

  // Affichage d'erreur
  if (error) {
    return (
      <div 
        className={`d-flex align-items-center justify-content-center border rounded bg-light ${className}`}
        style={{ width, height, ...style }}
      >
        <div className="text-center text-danger">
          <div className="mb-2">
            <span className="fs-3">⚠️</span>
          </div>
          <small>{error}</small>
        </div>
      </div>
    );
  }

  // Affichage de chargement
  if (!isLoaded) {
    return (
      <div 
        className={`d-flex align-items-center justify-content-center border rounded bg-light ${className}`}
        style={{ width, height, ...style }}
      >
        <div className="text-center text-muted">
          <div className="mb-2">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
          <small>Chargement de la carte...</small>
        </div>
      </div>
    );
  }

  // Rendu de la carte
  return (
    <div ref={containerRef} className={className} style={{ width, height, ...style }}>
      <div
        ref={mapRef}
        className={`border rounded ${className}`}
        style={{
          width: '100%',
          height: '100%',
          ...style
        }}
      />
    </div>
  );
};

export default GoogleMap;
